import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { AuthModalProvider } from "./contexts/AuthModalContext";
import AuthModal from "./components/auth/AuthModal";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./index.css";
import App from "./App.jsx";
import ReactGA from "react-ga4";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import ErrorBoundary from "./components/ErrorBoundary";
import { APP_ENV } from "./config";

Sentry.init({
  enabled: import.meta.env.PROD,
  dsn: import.meta.env.VITE_SENTRY_DSN,
  release: import.meta.env.VITE_SENTRY_RELEASE,
  environment: APP_ENV,
  sendDefaultPii: false,
  sampleRate: 1.0,
  tracesSampleRate: 0.2,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  ignoreErrors: [
    /ResizeObserver loop/,
    /ChunkLoadError/,
    /Loading chunk .* failed/,
    /Non-Error promise rejection/,
    "Object Not Found Matching Id",
  ],
  beforeSend(event) {
    const msg =
      event.exception?.values?.[0]?.value || "";

    const frames =
      event.exception?.values?.[0]?.stacktrace?.frames;
    if (frames && frames.length === 0) return null;

    if (
      /Failed to fetch/.test(msg) &&
      !event.request?.url
    )
      return null;

    if (/Failed to fetch/.test(msg))
      event.fingerprint = ["failed-to-fetch"];
    else if (/Load failed/.test(msg))
      event.fingerprint = ["load-failed"];
    else if (/NetworkError/.test(msg))
      event.fingerprint = ["network-error"];

    if (event.request?.headers) {
      delete event.request.headers["Authorization"];
      delete event.request.headers["Cookie"];
    }
    if (event.request?.cookies) delete event.request.cookies;

    return event;
  },
});

const queryClient = new QueryClient();

ReactGA.initialize("G-GX7CDE1928");

ReactGA.send({
  hitType: "pageview",
  page: window.location.pathname,
});

createRoot(document.getElementById("root")).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <AuthModalProvider>
              <App />
              <AuthModal />
            </AuthModalProvider>
            <ToastContainer
              position="top-right"
              autoClose={3000}
              hideProgressBar={false}
              newestOnTop
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="colored"
            />
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
